import { Component } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import { ACCESS_LEVELS } from '../constants/AccessLevels';
import { defaultDisableAccessToEveryoneFormattedMessage } from '../utils/AssetAccessUtils';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AccessToggle = ({
  accessLevel,
  selectedAccessLevel,
  onChange,
  disabled,
  customMessage
}) => {
  return /*#__PURE__*/_jsx(UIRadioInput, {
    name: `access-toggle-${accessLevel}`,
    "data-test-id": `access-toggle-${accessLevel}`,
    checked: accessLevel === selectedAccessLevel,
    onChange: () => onChange(accessLevel),
    disabled: disabled,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: customMessage || `ui-asset-management-lib.assetAccessStates.${accessLevel}`
    })
  });
};

// Used for controlling when you can set "everyone/selected users" to also be view and edit, or view only.
const GroupedPermissionsAccessToggles = ({
  selectedAccessLevel,
  baseAccessLevel,
  editLevel,
  viewLevel,
  onChange,
  disabled
}) => {
  const groupChecked = [editLevel, viewLevel].includes(selectedAccessLevel);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIRadioInput, {
      disabled: disabled,
      checked: groupChecked,
      onChange: () => onChange(editLevel),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `ui-asset-management-lib.assetAccessStates.${baseAccessLevel}`
      })
    }), groupChecked && /*#__PURE__*/_jsxs(View, {
      marginInlineStart: "400",
      as: "div",
      children: [/*#__PURE__*/_jsx(AccessToggle, {
        disabled: disabled,
        selectedAccessLevel: selectedAccessLevel,
        accessLevel: editLevel,
        onChange: onChange
      }), /*#__PURE__*/_jsx(AccessToggle, {
        disabled: disabled,
        selectedAccessLevel: selectedAccessLevel,
        accessLevel: viewLevel,
        onChange: onChange
      })]
    })]
  });
};
class AssetAccessLevelPicker extends Component {
  // Render just the option to select available to everyone or selected users and teams
  renderBaseAccessLevels() {
    const {
      value,
      onChange,
      disabled,
      isTeamsOnly,
      isUsersOnly,
      baseAccessValue,
      useAccessLanguage,
      disableAccessToEveryone,
      DisableAccessToEveryoneFormattedMessage
    } = this.props;
    return /*#__PURE__*/_jsxs(UIToggleGroup, {
      name: "access-level",
      children: [/*#__PURE__*/_jsx(UITooltip, {
        placement: "left",
        disabled: !disableAccessToEveryone,
        title: DisableAccessToEveryoneFormattedMessage && /*#__PURE__*/_jsx(DisableAccessToEveryoneFormattedMessage, {}),
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(AccessToggle, {
            selectedAccessLevel: value,
            accessLevel: ACCESS_LEVELS.EVERYONE,
            onChange: onChange,
            disabled: Boolean(disabled || disableAccessToEveryone)
          })
        })
      }), /*#__PURE__*/_jsx(AccessToggle, {
        selectedAccessLevel: value,
        accessLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS,
        onChange: onChange,
        disabled: disabled,
        customMessage: baseAccessValue && `ui-asset-management-lib.assetAccessStates.${baseAccessValue}` || isTeamsOnly && 'ui-asset-management-lib.assetAccessStates.SELECTED_TEAMS' || isUsersOnly && 'ui-asset-management-lib.assetAccessStates.SELECTED_USERS' || useAccessLanguage && 'ui-asset-management-lib.assetAccessStates.SELECTED_USERS_AND_TEAMS_ACCESS'
      })]
    });
  }

  // Render base options with an additional select option to make it private
  renderAccessLevelsWithPrivate() {
    const {
      value,
      onChange,
      disabled,
      disableAccessToEveryone,
      DisableAccessToEveryoneFormattedMessage,
      baseAccessValue,
      isTeamsOnly,
      isUsersOnly,
      useAccessLanguage
    } = this.props;
    return /*#__PURE__*/_jsxs(UIToggleGroup, {
      name: "access-level",
      children: [/*#__PURE__*/_jsx(AccessToggle, {
        selectedAccessLevel: value,
        accessLevel: ACCESS_LEVELS.PRIVATE,
        onChange: onChange,
        disabled: disabled
      }), /*#__PURE__*/_jsx(UITooltip, {
        placement: "left",
        disabled: !disableAccessToEveryone,
        title: DisableAccessToEveryoneFormattedMessage && /*#__PURE__*/_jsx(DisableAccessToEveryoneFormattedMessage, {}),
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(AccessToggle, {
            selectedAccessLevel: value,
            accessLevel: ACCESS_LEVELS.EVERYONE,
            onChange: onChange,
            disabled: Boolean(disabled || disableAccessToEveryone)
          })
        })
      }), /*#__PURE__*/_jsx(AccessToggle, {
        selectedAccessLevel: value,
        accessLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS,
        onChange: onChange,
        disabled: disabled,
        customMessage: baseAccessValue && `ui-asset-management-lib.assetAccessStates.${baseAccessValue}` || isTeamsOnly && 'ui-asset-management-lib.assetAccessStates.SELECTED_TEAMS' || isUsersOnly && 'ui-asset-management-lib.assetAccessStates.SELECTED_USERS' || useAccessLanguage && 'ui-asset-management-lib.assetAccessStates.SELECTED_USERS_AND_TEAMS_ACCESS'
      })]
    });
  }

  // Render the full list of options with the ability to set it as view only or view and edit for all users option
  renderAccessLevelsWithViewPermissions() {
    const {
      value,
      onChange,
      disabled,
      disableAccessToEveryone,
      DisableAccessToEveryoneFormattedMessage
    } = this.props;
    return /*#__PURE__*/_jsxs(UIToggleGroup, {
      name: "access-level",
      children: [/*#__PURE__*/_jsx(AccessToggle, {
        selectedAccessLevel: value,
        accessLevel: ACCESS_LEVELS.PRIVATE,
        onChange: onChange,
        disabled: disabled
      }), /*#__PURE__*/_jsx(UITooltip, {
        placement: "left",
        disabled: !disableAccessToEveryone,
        title: DisableAccessToEveryoneFormattedMessage && /*#__PURE__*/_jsx(DisableAccessToEveryoneFormattedMessage, {}),
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(GroupedPermissionsAccessToggles, {
            selectedAccessLevel: value,
            baseAccessLevel: ACCESS_LEVELS.EVERYONE,
            editLevel: ACCESS_LEVELS.EVERYONE_VIEW_AND_EDIT,
            viewLevel: ACCESS_LEVELS.EVERYONE_VIEW_ONLY,
            onChange: onChange,
            disabled: Boolean(disabled || disableAccessToEveryone)
          })
        })
      }), /*#__PURE__*/_jsx(GroupedPermissionsAccessToggles, {
        selectedAccessLevel: value,
        baseAccessLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS,
        editLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS_VIEW_AND_EDIT,
        viewLevel: ACCESS_LEVELS.SELECTED_USERS_AND_TEAMS_VIEW_ONLY,
        onChange: onChange,
        disabled: disabled
      })]
    });
  }
  render() {
    const {
      allowPrivate,
      allowSetEditLevel
    } = this.props;
    if (allowSetEditLevel) {
      return this.renderAccessLevelsWithViewPermissions();
    }
    if (allowPrivate) {
      return this.renderAccessLevelsWithPrivate();
    }
    return this.renderBaseAccessLevels();
  }
}
AssetAccessLevelPicker.propTypes = {
  // The currently selected access level.
  value: PropTypes.oneOf(Object.keys(ACCESS_LEVELS)).isRequired,
  // If the user can make the asset "view only", or set it to "view and edit" for everyone. Allowing to set the edit level includes the ability to set private.
  allowSetEditLevel: PropTypes.bool.isRequired,
  // If the user can assign the asset to just themselves "Private to me."
  allowPrivate: PropTypes.bool.isRequired,
  // Change the access level
  onChange: PropTypes.func.isRequired,
  // If the user is allowed to make changes to the base access levels
  disabled: PropTypes.bool.isRequired,
  isTeamsOnly: PropTypes.bool,
  isUsersOnly: PropTypes.bool,
  // Base/default asset access level to pick for label
  baseAccessValue: PropTypes.oneOf(Object.keys(ACCESS_LEVELS)),
  // If wanting to use language "access" instead of "edit"
  useAccessLanguage: PropTypes.bool,
  // Optional: a boolean flag that disables the various "everyone" access levels when set - default false
  disableAccessToEveryone: PropTypes.bool,
  // Optional: The formatted message which is displayed within the "everyone" access levels are disabled. This function needs to return a FormattedMessage.
  DisableAccessToEveryoneFormattedMessage: PropTypes.func.isRequired
};
AssetAccessLevelPicker.defaultProps = {
  DisableAccessToEveryoneFormattedMessage: defaultDisableAccessToEveryoneFormattedMessage
};
export default AssetAccessLevelPicker;